import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type MultiSelectOption = {
  value: string;
  label: string;
};

export type MultiSelectProps = {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Shown in the trigger when nothing is selected. */
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** Render an in-popover search box once options exceed this count (default 8). */
  searchThreshold?: number;
  searchPlaceholder?: string;
  noResultsLabel?: string;
};

/**
 * Reusable token/chip multi-select — the "Select one or more…" control from the
 * Trioty design. The trigger shows selected values as removable chips (or a
 * placeholder); tapping it opens a checkbox list in a body-portaled popover
 * anchored under the trigger. Escape / outside-click / a removed chip all keep
 * the parent `value` in sync via `onChange`.
 *
 * Use this instead of a text-first Combobox when the field is a bounded set of
 * options the user picks from a list (categories, tags, cities…).
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select one or more…",
  hasError,
  disabled,
  className,
  "aria-label": ariaLabel,
  searchThreshold = 8,
  searchPlaceholder = "Search…",
  noResultsLabel = "No results",
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    width: number;
    placement: "below" | "above";
  } | null>(null);

  const selected = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  const showSearch = options.length > searchThreshold;
  const filtered = useMemo(() => {
    if (!showSearch || !query) return options;
    const lower = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query, showSearch]);

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const placement = spaceBelow < 260 && r.top > spaceBelow ? "above" : "below";
    setRect({
      left: r.left,
      top: placement === "below" ? r.bottom + 6 : r.top - 6,
      width: r.width,
      placement,
    });
  }, []);

  // Measure synchronously on open so the popover never paints in the wrong spot.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => reposition();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Swallow it during capture so a host drawer/modal's own Escape
        // handler doesn't also fire and close the whole surface behind us.
        e.preventDefault();
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    // capture=true so we reposition against ANY scrolling ancestor (drawer body).
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, reposition]);

  // Reset the search box whenever the popover closes.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  };

  const remove = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  return (
    <div className={`relative w-full ${className ?? ""}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={hasError ? "true" : undefined}
        onClick={() => setOpen((o) => !o)}
        className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[10px] border bg-[color:var(--surface)] px-3 py-2 text-start transition ${
          hasError
            ? "border-[color:var(--error-500)]"
            : "border-[color:var(--border-light)] hover:border-[color:var(--border)] aria-expanded:border-[color:var(--primary-500)]"
        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
      >
        {selected.length > 0 ? (
          <span className="flex flex-wrap gap-1.5">
            {selected.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1.5 rounded-[8px] bg-[color:var(--surface-alt)] py-1 pe-1 ps-2.5 text-[12.5px] font-semibold text-[color:var(--text-primary)]"
              >
                {o.label}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${o.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(o.value);
                  }}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
                >
                  <XIcon />
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span className="text-[13.5px] text-[color:var(--text-muted)]">
            {placeholder}
          </span>
        )}
        <ChevronIcon />
      </button>

      {open && typeof document !== "undefined" && rect
        ? createPortal(
            <div
              ref={popoverRef}
              role="listbox"
              aria-multiselectable="true"
              style={{
                position: "fixed",
                left: rect.left,
                top: rect.top,
                width: rect.width,
                minWidth: 244,
                transform:
                  rect.placement === "above" ? "translateY(-100%)" : undefined,
                zIndex: 90,
              }}
              className="overflow-hidden rounded-[10px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] shadow-[var(--shadow-lg)]"
            >
              {showSearch ? (
                <div className="p-[9px_9px_5px]">
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 py-2 text-[16px] text-[color:var(--text-primary)] lg:text-[13px] focus:border-[color:var(--primary-500)] focus:outline-none"
                  />
                </div>
              ) : null}
              <div className="max-h-[224px] overflow-y-auto p-[5px]">
                {filtered.length === 0 ? (
                  <div className="px-2.5 py-3.5 text-center text-[12.5px] text-[color:var(--text-muted)]">
                    {noResultsLabel}
                  </div>
                ) : (
                  filtered.map((o) => {
                    const on = value.includes(o.value);
                    return (
                      <div
                        key={o.value}
                        role="option"
                        aria-selected={on}
                        onClick={() => toggle(o.value)}
                        className="flex cursor-pointer items-center gap-[11px] rounded-[7px] px-2.5 py-2.5 text-[13.5px] text-[color:var(--text-primary)] hover:bg-[color:var(--hover,var(--surface-alt))]"
                      >
                        {on ? (
                          <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[6px] bg-[color:var(--accent-500)] text-[color:var(--on-accent,#fff)]">
                            <CheckIcon />
                          </span>
                        ) : (
                          <span className="inline-block h-[18px] w-[18px] shrink-0 rounded-[6px] border-[1.5px] border-[color:var(--border-light)]" />
                        )}
                        <span className="min-w-0 flex-1 truncate">{o.label}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
